import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import {
  getAdminMatch,
  getStoryDetail,
  listRefinementSessions,
  getRefinementSession,
  listNotifyLog,
  listMatchEvents,
} from '../api/client';
import type {
  AdminMatchDetail,
  AdminStoryDetail,
  NotifyLogEntry,
  RefinementSession,
  RefinementSessionDetail,
  MatchBand,
} from '../api/types';
import ScoreBreakdown from '../components/scores/ScoreBreakdown';
import FlagChips from '../components/scores/FlagChips';
import SideBySide from '../components/comparison/SideBySide';
import PebbleEnvelopeView from '../components/envelope/PebbleEnvelopeView';
import RefinementSessionCard from '../components/refinement/RefinementSessionCard';
import ScoreSparkline from '../components/refinement/ScoreSparkline';
import RevisionDiff from '../components/refinement/RevisionDiff';
import QuestionsList from '../components/refinement/QuestionsList';
import RefinementActionBar from '../components/refinement/RefinementActionBar';
import NotifyLogTable from '../components/notify/NotifyLogTable';
import MatchEventsTable from '../components/matchevents/MatchEventsTable';
import { conversationPath } from '../components/matchevents/model';
import type { MatchEventsLog } from '../components/matchevents/model';
import AnalysisPanel from '../components/analyst/AnalysisPanel';

type TabKey = 'overview' | 'refinement' | 'doorbells' | 'raw' | 'ai';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'refinement', label: 'Refinement' },
  { key: 'doorbells', label: 'Doorbells' },
  { key: 'raw', label: 'Raw' },
  { key: 'ai', label: 'AI Analysis' },
];

export default function MatchDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as TabKey) || 'overview';
  const [tab, setTab] = useState<TabKey>(initialTab);

  const [match, setMatch] = useState<AdminMatchDetail | null>(null);
  const [storyA, setStoryA] = useState<AdminStoryDetail | null>(null);
  const [storyB, setStoryB] = useState<AdminStoryDetail | null>(null);
  const [sessionSummary, setSessionSummary] = useState<RefinementSession | null>(null);
  const [sessionDetail, setSessionDetail] = useState<RefinementSessionDetail | null>(null);
  const [notifyEntries, setNotifyEntries] = useState<NotifyLogEntry[]>([]);
  // PD-4: one log per side. Kept apart rather than merged so a row's "opens"
  // column and its Replay button unambiguously belong to one pebble — a merged
  // table would put A's and B's request keys under one heading, and the
  // acceptance is that both sides' events are reachable from this page.
  const [eventsA, setEventsA] = useState<MatchEventsLog | null>(null);
  const [eventsB, setEventsB] = useState<MatchEventsLog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Initial load
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);

    getAdminMatch(id)
      .then(async (m) => {
        setMatch(m);
        const [a, b, sessionsRes] = await Promise.all([
          getStoryDetail(m.storyA.id),
          getStoryDetail(m.storyB.id),
          listRefinementSessions({ matchId: m.id }),
        ]);
        setStoryA(a);
        setStoryB(b);
        const session = sessionsRes.sessions[0] ?? null;
        setSessionSummary(session);
        if (session) {
          const detail = await getRefinementSession(session.id);
          setSessionDetail(detail);
        }
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [id]);

  // Doorbell log — combined for both pebbles, after stories load.
  useEffect(() => {
    if (!storyA || !storyB) return;
    const a = storyA.pebble_id;
    const b = storyB.pebble_id;
    const fetches: Promise<{ entries: NotifyLogEntry[] }>[] = [];
    if (a) fetches.push(listNotifyLog(a, 50));
    if (b) fetches.push(listNotifyLog(b, 50));
    if (!fetches.length) return;
    Promise.all(fetches)
      .then((results) => {
        const all = results.flatMap((r) => r.entries);
        all.sort((x, y) => (x.createdAt < y.createdAt ? 1 : -1));
        setNotifyEntries(all);
      })
      .catch((e) => console.error('[notify_log]', e));
  }, [storyA, storyB]);

  // PD-4: the split plane's doorbell, per pebble.
  const pebbleA = storyA?.pebble_id ?? null;
  const pebbleB = storyB?.pebble_id ?? null;

  const loadEvents = useCallback(() => {
    if (pebbleA) listMatchEvents(pebbleA, 50).then(setEventsA).catch((e) => console.error('[match_events A]', e));
    if (pebbleB) listMatchEvents(pebbleB, 50).then(setEventsB).catch((e) => console.error('[match_events B]', e));
  }, [pebbleA, pebbleB]);

  useEffect(loadEvents, [loadEvents]);

  // Persist tab to URL.
  function selectTab(t: TabKey) {
    setTab(t);
    const next = new URLSearchParams(searchParams);
    next.set('tab', t);
    setSearchParams(next, { replace: true });
  }

  const finalScore = useMemo(() => {
    if (!match) return 0;
    return match.scores.final ?? match.scores.overall;
  }, [match]);

  const band: MatchBand = useMemo(() => scoreToBand(finalScore), [finalScore]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading match…</div>
      </div>
    );
  }

  if (error || !match || !storyA || !storyB) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        Error: {error ?? 'Match not found'}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Link to="/stories" className="text-sm text-pebbl-600 hover:text-pebbl-800">
        ← Back to stories
      </Link>

      {/* Header */}
      <div className="card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Match</h1>
            <div className="mt-1 flex items-center gap-3 flex-wrap text-sm text-gray-600">
              <code className="text-xs">{match.id}</code>
              <span className="text-gray-300">·</span>
              {storyA.pebble_id && (
                <span className="text-xs">A pebble <code className="text-xs">{storyA.pebble_id}</code></span>
              )}
              <span className="text-gray-300">↔</span>
              {storyB.pebble_id && (
                <span className="text-xs">B pebble <code className="text-xs">{storyB.pebble_id}</code></span>
              )}
              <span className="text-gray-300">·</span>
              <span className="text-xs">{new Date(match.createdAt).toLocaleString()}</span>
            </div>
            {/* PD-4's reverse direction: from a match into each side's
                conversation timeline (PD-2) and match-events log. */}
            <div className="mt-2 flex items-center gap-4 flex-wrap text-xs" data-testid="match-split-links">
              {storyA.pebble_id && (
                <span className="flex items-center gap-2">
                  <span className="text-gray-500">A</span>
                  <Link to={conversationPath(storyA.pebble_id)} className="text-pebbl-600 hover:text-pebbl-800">
                    timeline →
                  </Link>
                  <Link
                    to={`/match-events/${encodeURIComponent(storyA.pebble_id)}`}
                    className="text-pebbl-600 hover:text-pebbl-800"
                  >
                    match events →
                  </Link>
                </span>
              )}
              {storyB.pebble_id && (
                <span className="flex items-center gap-2">
                  <span className="text-gray-500">B</span>
                  <Link to={conversationPath(storyB.pebble_id)} className="text-pebbl-600 hover:text-pebbl-800">
                    timeline →
                  </Link>
                  <Link
                    to={`/match-events/${encodeURIComponent(storyB.pebble_id)}`}
                    className="text-pebbl-600 hover:text-pebbl-800"
                  >
                    match events →
                  </Link>
                </span>
              )}
            </div>
            {match.flags?.length ? (
              <div className="mt-2 flex items-center gap-1 flex-wrap">
                <FlagChips flags={match.flags} />
              </div>
            ) : null}
          </div>
          <div className="text-right">
            <div className="text-xs uppercase text-gray-500">Final score</div>
            <div className={`text-3xl font-bold ${bandTextClass(band)}`}>
              {(finalScore * 100).toFixed(0)}%
            </div>
            <div className={`text-xs font-medium ${bandTextClass(band)}`}>{band}</div>
          </div>
        </div>
        {match.consent && match.consent.state !== 'none' && (
          <div className="mt-4 pt-3 border-t border-gray-100">
            <div className="flex items-center gap-6 flex-wrap">
              <div className="text-xs uppercase text-gray-500">Consent</div>
              <ConsentBadge side="A" decision={match.consent.a} timestamp={match.consent.aAt} />
              <ConsentBadge side="B" decision={match.consent.b} timestamp={match.consent.bAt} />
              {match.consent.rejectReason && (
                <span className="text-xs text-red-600">
                  reason: {match.consent.rejectReason}
                </span>
              )}
              {match.consent.expiresAt && match.consent.state === 'consent_pending' && (
                <span className="text-xs text-gray-400">
                  expires {new Date(match.consent.expiresAt).toLocaleString()}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-1">
          {TABS.map((t) => {
            // Hide refinement tab if no session.
            if (t.key === 'refinement' && !sessionSummary) return null;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => selectTab(t.key)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  tab === t.key
                    ? 'border-pebbl-600 text-pebbl-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab content */}
      {tab === 'overview' && (
        <div className="space-y-6">
          <SideBySide storyA={storyA.story} storyB={storyB.story} />
          <ScoreBreakdown scores={match.scores} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {storyA.pebble_id && <PebbleEnvelopeView pebbleId={storyA.pebble_id} title="Pebble A envelope" />}
            {storyB.pebble_id && <PebbleEnvelopeView pebbleId={storyB.pebble_id} title="Pebble B envelope" />}
          </div>
        </div>
      )}

      {tab === 'refinement' && sessionSummary && sessionDetail && (
        <div className="space-y-6">
          <RefinementSessionCard session={sessionSummary} />
          <RefinementActionBar
            session={sessionSummary}
            onAfterAction={async (next) => {
              if (next) {
                setSessionSummary(next);
                const refreshed = await getRefinementSession(next.id);
                setSessionDetail(refreshed);
              }
            }}
          />
          <ScoreSparkline history={sessionDetail.scoreHistory} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <QuestionsList side="a" questions={sessionSummary.questionsA} />
              <RevisionList revisions={sessionDetail.storyARevisions} side="a" />
            </div>
            <div className="space-y-4">
              <QuestionsList side="b" questions={sessionSummary.questionsB} />
              <RevisionList revisions={sessionDetail.storyBRevisions} side="b" />
            </div>
          </div>
        </div>
      )}

      {tab === 'doorbells' && (
        <div className="space-y-6">
          {/* PD-4 — the split plane's doorbell, both sides. Above the legacy
              table because on a split pebble the node IS the doorbell. */}
          <div>
            <h3 className="font-semibold text-gray-700 mb-2">
              Match events — side A {storyA.pebble_id ? <code className="text-xs">{storyA.pebble_id}</code> : null}
            </h3>
            <MatchEventsTable
              log={eventsA}
              onAfterReplay={loadEvents}
              emptyLabel="No match events for side A."
            />
          </div>
          <div>
            <h3 className="font-semibold text-gray-700 mb-2">
              Match events — side B {storyB.pebble_id ? <code className="text-xs">{storyB.pebble_id}</code> : null}
            </h3>
            <MatchEventsTable
              log={eventsB}
              onAfterReplay={loadEvents}
              emptyLabel="No match events for side B."
            />
          </div>
          <div>
            <h3 className="font-semibold text-gray-700 mb-2">Doorbell log (legacy)</h3>
            <NotifyLogTable
              entries={notifyEntries}
              onAfterReplay={(entry) =>
                setNotifyEntries((prev) => prev.map((e) => (e.id === entry.id ? entry : e)))
              }
            />
          </div>
        </div>
      )}

      {tab === 'ai' && <AnalysisPanel matchId={match.id} />}

      {tab === 'raw' && (
        <div className="space-y-4">
          <div className="card">
            <h3 className="font-semibold text-gray-700 mb-2">Match row</h3>
            <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-3 overflow-x-auto">
              {JSON.stringify(match, null, 2)}
            </pre>
          </div>
          {sessionDetail && (
            <div className="card">
              <h3 className="font-semibold text-gray-700 mb-2">Refinement session</h3>
              <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-3 overflow-x-auto">
                {JSON.stringify(sessionDetail, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RevisionList({
  revisions,
  side,
}: {
  revisions: RefinementSessionDetail['storyARevisions'];
  side: 'a' | 'b';
}) {
  if (!revisions.length) {
    return (
      <div className="text-sm text-gray-500 italic">
        No revisions yet for side {side.toUpperCase()}.
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <div className="text-xs uppercase text-gray-500">
        Side {side.toUpperCase()} revisions
      </div>
      {revisions.map((rev, idx) => (
        <RevisionDiff
          key={rev.id}
          current={rev}
          previous={revisions[idx + 1] ?? null}
          defaultExpanded={idx < 1}
        />
      ))}
    </div>
  );
}

function scoreToBand(score: number): MatchBand {
  if (score < 0.4) return 'fail';
  if (score < 0.7) return 'refinement';
  return 'consent';
}

function bandTextClass(band: MatchBand): string {
  if (band === 'fail') return 'text-red-600';
  if (band === 'refinement') return 'text-amber-600';
  return 'text-green-600';
}

function ConsentBadge({
  side,
  decision,
  timestamp,
}: {
  side: 'A' | 'B';
  decision: 'accepted' | 'declined' | null;
  timestamp: string | null;
}) {
  if (!decision) {
    return (
      <span className="inline-flex items-center gap-1 text-xs">
        <span className="text-gray-500">Side {side}:</span>
        <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded">pending</span>
      </span>
    );
  }
  const isAccepted = decision === 'accepted';
  const cls = isAccepted
    ? 'bg-green-100 text-green-800'
    : 'bg-red-100 text-red-800';
  return (
    <span className="inline-flex items-center gap-1 text-xs">
      <span className="text-gray-500">Side {side}:</span>
      <span className={`px-2 py-0.5 rounded font-medium ${cls}`}>
        {decision}
      </span>
      {timestamp && (
        <span className="text-gray-400">
          {new Date(timestamp).toLocaleString()}
        </span>
      )}
    </span>
  );
}
